/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bell, Search, Menu, User } from 'lucide-react';
import { THEME_CONFIG } from '@/config/themeConfig';

/**
 * BrandLivePreview — sticky mockup that mirrors the current form values so
 * the admin can see the branding change in real-time before hitting Save.
 *
 * Purposely NOT a full app iframe — it's a curated set of building blocks
 * (header, hero, buttons, badges) that touch the most-visible colour and
 * logo surfaces. Every colour is inlined so we don't require a page reload.
 */
export const BrandLivePreview = ({ settings }) => {
  const {
    clinicName = 'La tua clinica',
    clinicShortName,
    tagline = 'Il tuo slogan',
    logoUrl,
    primaryColor = THEME_CONFIG.colors.primary,
    primaryColorAlt = THEME_CONFIG.colors.secondary,
    accentColor = THEME_CONFIG.colors.accent,
  } = settings || {};

  const gradient = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorAlt} 100%)`;

  return (
    <div className="lg:sticky lg:top-4 space-y-3" data-testid="brand-live-preview">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          Anteprima live
        </h3>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
          Non salvato
        </Badge>
      </div>

      {/* ---------------- Mock: App header ---------------- */}
      <Card className="overflow-hidden shadow-elev-md">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border" style={{ background: '#fff' }}>
          <Menu className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="logo"
                className="h-7 w-7 rounded object-contain bg-muted"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div
                className="h-7 w-7 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{ background: gradient }}
                aria-hidden="true"
              >
                {(clinicShortName || clinicName || 'S').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-bold truncate" style={{ color: primaryColor }}>
                {clinicName}
              </div>
              <div className="text-[10px] text-muted-foreground truncate">
                {tagline}
              </div>
            </div>
          </div>
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="relative">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <span
              className="absolute -top-1 -right-1 h-2 w-2 rounded-full"
              style={{ background: accentColor, boxShadow: `0 0 0 1.5px #fff` }}
            />
          </div>
          <div
            className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold"
            style={{ background: gradient }}
          >
            <User className="h-3.5 w-3.5" />
          </div>
        </div>

        {/* ---------------- Mock: Hero banner ---------------- */}
        <div className="p-4 text-white" style={{ background: gradient }}>
          <div className="text-[10px] uppercase tracking-wider opacity-80">
            Benvenuto in
          </div>
          <div className="font-display font-bold text-lg leading-tight mt-0.5">
            {clinicName}
          </div>
          <div className="text-xs opacity-90 mt-1 line-clamp-2">
            {tagline}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              tabIndex={-1}
              className="text-xs font-semibold px-3 py-1.5 rounded-md text-white shadow-sm transition-transform hover:scale-[1.02]"
              style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)' }}
            >
              Prenota visita
            </button>
            <button
              type="button"
              tabIndex={-1}
              className="text-xs font-semibold px-3 py-1.5 rounded-md"
              style={{ background: '#fff', color: primaryColor }}
            >
              Scopri di più
            </button>
          </div>
        </div>

        {/* ---------------- Mock: Body chips ---------------- */}
        <div className="p-4 space-y-3 bg-card">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white"
              style={{ background: primaryColor }}
            >
              Primario
            </span>
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white"
              style={{ background: primaryColorAlt }}
            >
              Secondario
            </span>
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
              style={{ background: accentColor, color: primaryColor }}
            >
              Accento
            </span>
          </div>

          <div className="rounded-lg border border-border p-3 flex items-center gap-3">
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ background: gradient }}
            >
              DR
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">Dr. Elena Rossi</div>
              <div className="text-[11px] text-muted-foreground">Cardiologia · 4.9★</div>
            </div>
            <button
              type="button"
              tabIndex={-1}
              className="text-[11px] font-bold px-3 py-1.5 rounded-md text-white"
              style={{ background: primaryColor }}
            >
              Prenota
            </button>
          </div>

          <div
            className="rounded-lg p-3 border-l-4"
            style={{ borderColor: accentColor, background: `${accentColor}22` }}
          >
            <div className="text-[11px] font-bold" style={{ color: primaryColor }}>
              Suggerimento
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
              Le tue modifiche saranno applicate a tutti gli utenti dopo il salvataggio.
            </div>
          </div>
        </div>
      </Card>

      <p className="text-[11px] text-muted-foreground leading-relaxed px-1">
        L'anteprima aggiorna logo, nome, colori e accento istantaneamente.
        Font, layout e componenti reali sono ricaricati dopo il salvataggio.
      </p>
    </div>
  );
};

export default BrandLivePreview;
